/* eslint-disable */
import type { Money } from "../../common/currency.pb";

/**
 * Represents a localizable string that can be translated to a different locale with variable arguments replaced inside the string.
 * For newly declared fields that are intended to be human-readable strings, the LocalizedString wrapper should be used instead.
 *
 * This proto should be used for localizing only sync entities / sync values in the ClientSync service,
 * or localizing protos in requests to postmaster for sending localized messages to customers.
 * The above cases are localized automatically by the ClientSync and Postmaster services.
 * If you need to use this proto anywhere else, they will not be localized automatically.
 * Please ask for help in #cash-localization-eng-help.
 */
export interface LocalizableString {
  /**
   * Localization key that points to the translated string.
   * The string should be in ICU format. https://en.wikipedia.org/wiki/International_Components_for_Unicode
   * e.g. key could be "clientsync.hello" where the English string is "Hello, {name}!"
   */
  key?: string;
  /**
   * Map of argument names to values that should be replaced on the localized string. e.g. map of "name" to "Jack"
   * Using a repeated custom message instead of the native map type because Cash iOS does not support it: https://cash.slack.com/archives/C01M5K4DM3P/p1628721166209800?thread_ts=1628720733.202800&cid=C01M5K4DM3P
   */
  arguments?: LocalizableStringLocalizableStringArgument[];
  /** Options for how to format Money parameters */
  moneyFormatter?: LocalizableStringMoneyFormatter;
  /**
   * Option for how to format the timezone portion of Timestamp parameters
   * For timezone, use standard tz database names (https://www.iana.org/time-zones)
   * For convenience: https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
   * If timezone is not provided, then timezone is derived from the Time-Zone request header from the client.
   */
  timezone?: string;
}

/**
 * These correspond to the formatters available in our MoneyFormatter library.
 * See: https://github.com/squareup/cash-mobile-multiplatform/blob/a6e39335aa617110c7b967e260c2041e1a77bd33/money-formatter/src/commonMain/kotlin/com/squareup/cash/common/moneyformatter/MoneyFormatter.kt#L245
 */
export enum LocalizableStringMoneyFormatter {
  MONEY_FORMATTER_UNSPECIFIED = "MONEY_FORMATTER_UNSPECIFIED",
  STANDARD = "STANDARD",
  ACCOUNTING = "ACCOUNTING",
  CODE = "CODE",
  COMPACT = "COMPACT",
  COMPACT_CODE = "COMPACT_CODE",
  COMPACT_POSITIVE_SIGN_ONLY = "COMPACT_POSITIVE_SIGN_ONLY",
}

export interface LocalizableStringLocalizableStringArgument {
  name?: string;
  string_value?: string | undefined;
  long_value?: number | undefined;
  double_value?: number | undefined;
  money_value?:
    | Money
    | undefined;
  /**
   * Was "google.protobuf.Timestamp timestamp_value = 6;"
   * Caused JSON serialization errors in various Cash services as the Timestamp proto is generated to java.time.Instant for Kotlin/Java with Wire.
   * Rather than ensuring that all services are using the correct JSON adapter to serialize the generated proto, we are simplifying the proto instead.
   * See https://cash.slack.com/archives/C02JU5EN33Q/p1675280149224509?thread_ts=1675181697.468959&cid=C02JU5EN33Q
   * We do not need nanosecond precision, only seconds, when displaying formatted time to the customer.
   * Replaced with "int64 timestamp_seconds_value = 6;" at the same field number.
   * Not reserving the field number instead because oneof doesn't support reserved.
   * Not deprecating the field because callers that do JSON serialization will still have the same issue.
   *
   * Represents seconds of UTC time since Unix epoch 1970-01-01T00:00:00Z. Must be from 0001-01-01T00:00:00Z to 9999-12-31T23:59:59Z inclusive.
   */
  timestamp_seconds_value?:
    | number
    | undefined;
  /**
   * Please use best judgement when choosing to nest localizable strings.
   * Some languages might not be structured in the same way that make it
   * difficult or impossible to translate depending on how it is used.
   */
  nested_localizable_string_value?: LocalizableString | undefined;
}

/**
 * Represents a string that should be localized or is localized.
 * For newly declared fields that are intended to be human-readable strings, this LocalizedString wrapper should be used.
 *
 * This proto should be used for localizing only sync entities / sync values in the ClientSync service,
 * or localizing protos in requests to postmaster for sending localized messages to customers.
 * The above cases are localized automatically by the ClientSync and Postmaster services.
 * If you need to use this proto anywhere else, they will not be localized automatically.
 * Please ask for help in #cash-localization-eng-help.
 */
export interface LocalizedString {
  /** Sets the translated_value field with the value from localizing this string. */
  localizable_string?: LocalizableString;
  /** Translated string value after localizing localizable_string. */
  translated_value?: string;
}
